import React, { useEffect, useState } from 'react';
import { Star, Quote } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

// Real reviews from the Google Business listing, served by the backend from a
// once-a-day cache. This section used to render four invented testimonials with
// stock photographs; anything shown here now comes from a real customer or the
// section does not render at all.
const TestimonialsSection = () => {
  const { isAuthenticated } = useAuth();
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .get('/reviews/google')
      .then(({ data: d }) => !cancelled && setData(d))
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
    };
  }, []);

  const handleStartShipment = () => {
    window.location.href = isAuthenticated ? '/dashboard' : '/?register=true';
  };

  const renderStars = (rating) =>
    Array.from({ length: 5 }).map((_, i) => (
      <Star
        key={i}
        className={`h-4 w-4 ${i < Math.round(rating || 0) ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
      />
    ));

  const reviews = data?.reviews || [];

  // No reviews yet, reviews not configured, or Google unreachable with nothing
  // cached: show nothing rather than an empty shell or invented filler.
  if (failed || !data || !reviews.length) return null;

  return (
    <section className="py-20 bg-gradient-to-br from-orange-50 to-blue-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <Badge variant="secondary" className="mb-4 bg-green-100 text-green-800 border-green-200">
            Google Reviews
          </Badge>
          <h2 className="text-4xl font-bold text-slate-900 mb-6">What Our Customers Say</h2>

          {/* Rating only - the client asked for the count to stay off the page. */}
          {data.rating ? (
            <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 mt-8">
              <div className="flex items-center space-x-1">{renderStars(data.rating)}</div>
              <div className="text-2xl font-bold text-slate-900">{Number(data.rating).toFixed(1)}</div>
              <div className="text-slate-600">on Google</div>
            </div>
          ) : null}
        </div>

        {/* items-start: otherwise a one-line review is stretched to match a long
              one beside it and sits in a pool of empty card. */}
        <div className="grid grid-cols-1 md:grid-cols-2 items-start gap-8 mb-12">
          {reviews.map((review, i) => (
            <Card key={`${review.author}-${review.time || i}`} className="border-0 shadow-lg bg-white">
              <CardContent className="p-8">
                <Quote className="h-8 w-8 text-orange-200 mb-4" aria-hidden="true" />
                <div className="flex items-center space-x-1 mb-4">{renderStars(review.rating)}</div>
                <p className="text-slate-700 leading-relaxed mb-6">{review.text}</p>
                <div className="flex items-center space-x-3">
                  {review.profile_photo ? (
                    <img
                      src={review.profile_photo}
                      alt=""
                      className="h-10 w-10 rounded-full object-cover"
                      loading="lazy"
                      referrerPolicy="no-referrer"
                    />
                  ) : null}
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-900 truncate">{review.author}</p>
                    {review.relative_time ? (
                      <p className="text-sm text-slate-500">{review.relative_time}</p>
                    ) : null}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="text-center space-y-4">
          {/* Google's terms require the reviews to link back to the listing. */}
          {data.place_url ? (
            <p>
              <a
                href={data.place_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-600 hover:text-orange-600 underline underline-offset-4"
              >
                Review us on Google
              </a>
            </p>
          ) : null}
          <Button onClick={handleStartShipment} className="bg-[#F17221] hover:bg-[#d9661d]">
            Start Your Shipment
          </Button>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
